import * as React from 'react';
import { filter, concat, set, includes, pickBy } from 'lodash';
import { createApplication, WorkstreamTrafficControl, entityConfigs, appSectionConfigs as coreAppSectionConfigs, Icons, DataTableNameValue, DataTable, Content, IconTitle, graphql, entityConnectionTypeSetsDefault, entityCoreFormFields, EntityMultiValueList, ITEMS_PER_PAGE } from '@shapeable/core';
import { ApplicationLogo, ApplicationLogoInverted } from './components';
import { themes } from '@shapeable/theme';
const { Citation, IndicatorValue, IndicatorResult, FeedEntry, Video, Event, Page, Post, Region, Indicator, Organisation, Place, Location, Person, Economy } = entityConfigs;

import { 
  DisturbanceType, ErrorModel, Genus, 
  Motivation, Observation, Methodology, OrganisationPledge, PledgeMetric, MethodologyType, ImpactType, Reason, RestorationProject,
  Species, SpeciesType, TransplantAdherenceMethod, TransplantLifeStage, TransplantSource, EcologicalPrinciple, RegionType,
} from './config';
import { PersonDetailsLayout } from './components/person-details-layout';
import { OrganisationDetailsLayout } from './components/organisation-details-layout';
import { EconomyDetailsLayout } from './components/economy-details-layout';

const theme = themes.OCEAN_THEME;
const appSectionConfigs = coreAppSectionConfigs;
const { platform } = appSectionConfigs;

// reduce appSection configs

if (appSectionConfigs.community) {
  appSectionConfigs.community.label = 'Community';
} 

// appSectionConfigs.library.children = [
//   ...filter(appSectionConfigs.library.children, (child: any) => includes(["insights", "feed-entries"], child.id)),
//   {
//     id: 'citations',
//     type: 'list',
//     entityTypeName: 'Citation',
//     label: "Citations",
//   },
  
// ];

if (appSectionConfigs.community) {
  appSectionConfigs.community.children = filter(appSectionConfigs.community.children, (child: any) => includes(["organisations", "people"], child.id));
}

if (appSectionConfigs.places) {
  const placeChildren = filter(appSectionConfigs.places.children, (child: any) => includes(["regions", "place"], child.id));
  const hasPlace = placeChildren.some((child: any) => child.id === 'place' || child.entityTypeName === 'Place');

  if (!hasPlace) {
    placeChildren.push({
      id: 'place',
      type: 'list',
      entityTypeName: 'Place',
      label: 'Sites',
    });
  }

  appSectionConfigs.places.children = placeChildren;
} 

if (appSectionConfigs.participate) {
  appSectionConfigs.participate.children = [
    {
      id: 'restoration-projects',
      type: 'list',
      entityTypeName: 'RestorationProject',
      label: "Restoration Projects",
      
    },
    {
      id: 'teams',
      type: 'list',
      entityTypeName: 'Team',
    },
    {
      id: 'events',
      type: 'list',
      entityTypeName: 'Event',
    },
  ];
}

// bump
delete appSectionConfigs.teams;
delete appSectionConfigs.following;
delete appSectionConfigs.challengesGoals;
// delete appSectionConfigs.places;

if (Organisation?.form?.fieldSets) {
  Organisation.form.fieldSets = filter(Organisation?.form?.fieldSets, fs => !includes(['stats', 'pledges'], fs.name));
}

if (Organisation?.form?.fieldSets) {
  Organisation.form.fieldSets.push({ name: 'pledges' });
}

delete entityConfigs.Organisation?.form?.fields?.states;

delete entityConfigs.Organisation?.form?.fields?.angelListUrl;

delete entityConfigs.Organisation?.form?.fields?.crunchbaseUrl;

if (Video) {
  if (Video?.form?.fieldSets) {
    Video.form.fieldSets = [
    // ...filter(Video.form.fieldSets, (fs) => !includes(['overview', 'challenges-and-goals', 'community'], fs.name)),
    { name: 'overview' },
    { name: 'taxonomies', label: 'Challenges & Goals' },
    { name: 'places' },
    { name: 'community' },
    { name: 'library' },
  ];
}

  Video.extraFields = {
    genera: { type: 'linkedMultiple', entityTypeName: 'Genus' },
    methodologies: { type: 'linkedMultiple', entityTypeName: 'Methodology' },
    economies: { type: 'linkedMultiple', entityTypeName: 'Economy' },
    regions: { type: 'linkedMultiple', entityTypeName: 'Region', fieldSet: 'places' },
    traditionalLands: { type: 'linkedMultiple', entityTypeName: 'TraditionalLand', fieldSet: 'places' },
    feedEntries: { type: 'linkedMultiple', entityTypeName: 'FeedEntry', fieldSet: 'library' },
    posts: { type: 'linkedMultiple', entityTypeName: 'Post', fieldSet: 'library' },
  };

  if (Video?.form?.fields?.advertisements) {
    delete Video.form.fields.advertisements;
  }

  if (Video?.form?.fields?.sdgs) {
    delete Video.form.fields.sdgs;
  }

  if (Video?.form?.fields?.subTopics) { 
    delete Video.form.fields.subTopics;
  }
}

if (Citation) {
  Citation.extraFields = {
    genera: { type: 'linkedMultiple', entityTypeName: 'Genus' },
    species: { type: 'linkedMultiple', entityTypeName: 'Species' },
    methodologies: { type: 'linkedMultiple', entityTypeName: 'Methodology' },
    people: { type: 'linkedMultiple', entityTypeName: 'Person' },
    economies: { type: 'linkedMultiple', entityTypeName: 'Economy' },
    organisations: { type: 'linkedMultiple', entityTypeName: 'Organisation' },
    regions: { type: 'linkedMultiple', entityTypeName: 'Region' },
  }
}

if (Organisation?.form?.fields?.regions) {
  Organisation.form.fields.regions.description = 'Which region, state, province or administrative body is this organization located?';
}

if (Organisation) {
  Organisation.extraFields = {
    pledges: { type: 'linkedTable', entityTypeName: 'OrganisationPledge', description: "Record any pledges this {{ORGANISATION}} has made to help the alliance achieve their goals.", fieldSet: 'pledges' },
  }
}

if (Place) {
  Place.label = "Site";
  Place.slug = "restoration-site";
}

if (Place?.list?.banner?.content) {
  Place.list.banner.content = "Record the places where Restoration Projects are undertaken.";
}

if (Place?.form?.fields?.city) {
  Place.form.fields.city.description = "What suburb or administrative areas is this site in? Be specific, what is the finest detail you would write on a postal address, avoid using large cities";
}
if (Place?.form?.fields?.regions) {
  Place.form.fields.regions.label = "{{REGION}}";
  Place.form.fields.regions.description = "Which region is this site located in, use the state, province, district, prefecture, etc, as applicable";
  Place.form.fields.regions.required = true;
}

if (Location?.form?.fieldSets) {
  Location.form.fieldSets = filter(Location.form.fieldSets, fs => !includes(['stats', 'narratives'], fs.name));
}

if (Person) {
  Person.extraFields = {
    motivations: { type: 'linkedMultiple', entityTypeName: 'Motivation', description: "Which {{MOTIVATIONS}} does this {{PERSON}} pursue?", fieldSet: 'expertise' },
    species: { type: 'linkedMultiple', entityTypeName: 'Species', fieldSet: 'expertise', description: "Which {{SPECIES}} does this {{PERSON}} have expertise with?" },
    methodologies: { type: 'linkedMultiple', entityTypeName: 'Methodology', description: "Which {{METHODOLOGIES}} does this {{PERSON}} have expertise with?", fieldSet: 'expertise' },
  }
}

if (Person?.form?.fields?.bio) {
  Person.form.fields.bio.description = "Describe the career of this person and how they are involved in kelp forest conservation and restoration.";
}

if (Person?.form?.fields?.states) {
  delete Person.form.fields.states;
}


if (Person?.details?.queryFields) {
  Person.details.queryFields = graphql`
    ${Person.details.queryFields}
    contactForRestorationProjects { ...EntityValueFields }
    motivations { ...EntityValueFields } 
    species { ...EntityValueFields }
    methodologies { ...EntityValueFields }
  `;
}


if (Organisation?.details?.queryFields) {
  Organisation.details.queryFields = graphql`
    ${Organisation.details.queryFields}
    restorationProjects { ...EntityValueFields place { ...EntityValueFields } } 
    species { ...EntityValueFields } 
    pledges { 
      ...EntityValueFields 
      id value 
      latitude longitude 
      metric { 
        id slug name title 
        unit { id shorthand name } 
      } 
    } 
    methodologies { ...EntityValueFields }
  `;
}


if (Economy?.details?.queryFields) {
  Economy.details.queryFields = graphql`
    ${Economy.details.queryFields}
    restorationProjects { ...EntityValueFields place { ...EntityValueFields } } 
  `;
}

if (Organisation?.filters?.fields) {
  Organisation.filters.fields = {
  country: { entityTypeName: 'Economy' },
  regions: { entityTypeName: 'Region' },
    type: { entityTypeName: 'OrganisationType' },
    methodologies: { entityTypeName: 'Methodology' },
    species: { entityTypeName: 'Species' },
  };
}

if (Person?.components?.details) {
  Person.components.details = PersonDetailsLayout;
}
if (Organisation?.components?.details) {
  Organisation.components.details = OrganisationDetailsLayout;
}
if (Economy?.components?.details) {
  Economy.components.details = EconomyDetailsLayout;
}


if (IndicatorValue?.table) {
  IndicatorValue.table = {
    fieldNames: ["indicator", "endValue", "startValue"]
  };
}

if (IndicatorResult) {
  IndicatorResult.extraFields = {
    label: { type: 'text', label: 'Segment label', description: 'When several results share the same indicator use this to name the segment.', recommended: false },
  };
}


if (IndicatorValue?.form?.fieldSets) {
  IndicatorValue.form.fieldSets = [
  { 
    name: 'overview', 
    label: 'Restoration Site', 
    intro: "The values under this 'Restoration Site' tab represent the site where the restoration activity occurred, e.g. where you placed the transplants or removed the sea urchins. The values under the 'Control Site' tab represent another reference location where no restoration activity occurred – i.e. they are used to determine if the restoration activity had an effect or if changes were due to environmental variation.", 
    outro: "Each indicator value is the variable that was recorded as a result of the restoration – it may be a kelp or animal population or social factor related to restoration, e.g. (kelp/m2, jobs/year, biomass of fish/year). There may be multiple values for the observation, for example all three of the previous examples might apply to this observation." 
  },
  { name: 'dates' },
  { name: 'control-site', intro: "The values under this tab represent another reference location where no restoration activity occurred – i.e. they are used to determine if the restoration activity had an effect or if changes were due to environmental variation.",  },
  ];
}

entityConfigs.FeedEntry.list = {
  ...(entityConfigs.FeedEntry.list || {}),
  banner: {
    content: 'Read articles and news about the latest developments in kelp forest ecosystems.',
  }
}

if (FeedEntry) {
  FeedEntry.extraFields = {
    genera: { type: 'linkedMultiple', entityTypeName: 'Genus' },
    species: { type: 'linkedMultiple', entityTypeName: 'Species' },
    methodologies: { type: 'linkedMultiple', entityTypeName: 'Methodology' },
    regions: { type: 'linkedMultiple', entityTypeName: 'Region', fieldSet: 'places', description: "Does this {{FEED_ENTRY}} have any associated {{REGIONS}}?" },
  };
}

if (Page) {
  Page.extraFields = {
    featurePosts: { type: 'linkedMultiple', entityTypeName: 'Post', label: "Featured Posts" },
    methodologies: { type: 'linkedMultiple', entityTypeName: 'Methodology', label: "Methodologies" },
    species: { type: 'linkedMultiple', entityTypeName: 'Species', label: "Species" },
    genera: { type: 'linkedMultiple', entityTypeName: 'Genus', label: "Genera" },
    economies: { type: 'linkedMultiple', entityTypeName: 'Economy', label: "Countries" },
  }
}

if (Post) {
  Post.extraFields = {
    regions: { type: 'linkedMultiple', entityTypeName: 'Region', description: "Are there {{REGIONS}} associated with this post?" },
  }
}

if (Indicator) {
  Indicator.extraFields = {
    suffixLabel: { type: 'text', description: "A friendly label displayed after the metric value (e.g., `genera`, `ha restored`, `projects`). Use this instead of technical units when a more descriptive label is needed." },
    image: { type: 'linked', entityTypeName: 'ImageAsset', description: "An image that represents this {{INDICATOR}}." },
    content: { type: 'longText', height: '100px' },
  };
}

if (IndicatorValue) {
  IndicatorValue.extraFields = {
    
    ...entityCoreFormFields,
    indicator: { type: 'linked', required: true, description: "Which {{INDICATOR}} does this value measure?", entityTypeName: 'Indicator' },
    
    endValue: { type: 'text', label: "Final Mean Value", description: "What is the mean value of the last recorded observation?" },
    endError: { type: 'text', label: "Final Error Bound", description: "What is the error value of the last recorded observation?", recommended: false },
    endErrorModel: { type: 'linked', entityTypeName: 'ErrorModel', label: "Ending Measure Of Error", description: 'How is the error recorded?' },
    endCount: { type: 'int', label: "Final Sample Count", description: "What is the sample size of the last recorded observation, e.g. how many quadrats were used or transects were observed", recommended: false },

    startValue: { type: 'text', label: "Initial Mean Value", description: "What was the mean value before or at the start of restoration?" },
    startError: { type: 'text', label: "Initial Error Bound", description: "What is the error value for the starting mean?", recommended: false },
    startErrorModel: { type: 'linked', entityTypeName: 'ErrorModel', label: "Initial Measure Of Error", description: 'How is Error Reported for the starting mean?' },
    startCount: { type: 'int', label: "Initial Sample Count", description: "How many samples were taken for the starting mean?", recommended: false },

    startDate: { type: 'date', recommended: false, description: "When was the initial recording made?", fieldSet: 'dates' },
    endDate: { type: 'date', recommended: false, description: "When was the most recent or last recording made?", fieldSet: 'dates' },

    controlEndValue: { type: 'text', fieldSet: 'control-site', label: "Control Ending Mean Value",  description: "What was the starting mean value of all samples taken in the control site?" },
    controlEndError: { type: 'text', fieldSet: 'control-site', label: "Control Ending Error Bound", description: "What is the error value for the ending mean in the control site?"  },
    controlEndErrorModel: { type: 'linked', fieldSet: 'control-site', entityTypeName: 'ErrorModel', label: "Control Ending Measure Of Error", description: 'How is Error Reported for the control ending mean?' },
    controlEndCount: { type: 'int', fieldSet: 'control-site', label: "Control Ending Sample Count", description: "How many samples were taken for the ending mean in the control site?", },

    controlStartValue: { type: 'text', fieldSet: 'control-site', label: "Control Starting Mean Value", description: "What was the starting mean value of all samples taken in the control site?" },
    controlStartError: { type: 'text', fieldSet: 'control-site', label: "Control Starting Error Bound", description: "What is the error value for the starting mean in the control site?"  },
    controlStartErrorModel: { type: 'linked', fieldSet: 'control-site', entityTypeName: 'ErrorModel', label: "Control Starting Measure Of Error", description: 'How is Error Reported for the control starting mean?' },
    controlStartCount: { type: 'int', fieldSet: 'control-site', label: "Control Starting Sample Count", description: "How many samples were taken for the starting mean in the control site?",  },
  }
}

if (Region) {
  Region.form.fieldSets = [
    { name: 'overview' },
    { name: 'key-content' },
    { name: 'data' },
    { name: 'indicators' },
  ];

  if (Region?.filters?.fields) {
    Region.filters.fields = {
      economy: { entityTypeName: 'Economy' },
      type: { entityTypeName: 'RegionType' },
    }
  }

  Region.extraFields = {
    type: { type: 'linked', entityTypeName: 'RegionType', description: "What type of {{REGION}} is this?" },
    banner: { type: 'banner' },
    authors: { type: 'linkedMultiple', entityTypeName: 'Person', label: 'Authors', description: "Who are the main contributors for this {{REGION}}?" },
    parent: { type: 'parent', recommended: false },
    latitude: { type: 'float' },
    longitude: { type: 'float' },
    zoomLevel: { type: 'float', label: "Map Zoom Level", description: "What zoom level should the map default to when focusing on this {{REGION}}? NOTE: You can find the zoom level on the Kelp Forst Alliance map in the bottom left corner." },

    populationNearKelp: { type: 'int', label: "Population Living Within 50KM of Kelp Forests", fieldSet: 'data' },
    coastlineLengthkm: { type: 'float', label: "Coastline Length (KM)", fieldSet: 'data' },
    gdpWithin50kmKelp: { type: 'float', label: "GDP(B) Within 50KM of Kelp", fieldSet: 'data' },
    oceanWarmingRate: { type: 'float', label: "Ocean Warming by 2100 (°C)", fieldSet: 'data' },
    kelpAreaHectares: { type: 'float', label: "Kelp Area (Ha)", fieldSet: 'data' },
    carbonSequestrationMegaTonnes: { type: 'float', label: "Carbon Sequestered (MtCO2e)", fieldSet: 'data' },
    species: { type: 'linkedMultiple', entityTypeName: 'Species', label: "Key Species", description: "Which {{SPECIES}} are key to this {{REGION}}'s kelp forest ecosystems?" },

    keyServices: { type: 'longText', height: '200px', fieldSet: 'key-content', label: "Key Developments" },
    keyOpportunities: { type: 'longText', height: '200px', fieldSet: 'key-content' },
    whatHasWorked: { type: 'longText', height: '200px', label: "What Has Worked To Accelerate Action", fieldSet: 'key-content' },
    keyThreats: { type: 'longText', height: '200px', fieldSet: 'key-content' },
    keyMessage: { type: 'longText', height: '100px', fieldSet: 'key-content' },
    keyEcosystemServices: { type: 'longText', height: '200px', fieldSet: 'key-content', label: "Key Services" },
    responses: { type: 'longText', height: '200px', fieldSet: 'key-content' },
    capacity: { type: 'longText', height: '200px', fieldSet: 'key-content' },
    keyMessageImage: { type: 'linked', entityTypeName: 'ImageAsset', label: "Key Message Background Image", fieldSet: 'key-content'},

    regionIndicatorResults: { type: 'linkedTable', entityTypeName: 'IndicatorResult', fieldSet: 'indicators', label: "Region Indicator Results", description: "Which {{INDICATOR_RESULTS}} are associated with this {{REGION}}?" },
  }
}

if (Event) {
  Event.groupName = "Participate";
};
  
const entityConnectionTypeSets = entityConnectionTypeSetsDefault;

if (entityConnectionTypeSets?.default?.mappings?.Person) {
  entityConnectionTypeSets.default.mappings.Person.push("RestorationProject");
}

entityConnectionTypeSets.default.mappings.RestorationProject = [
  "Person", "Organisation"
];

export const application = createApplication({
  theme,
  entityConfigs: {
    // ...entityConfigs,
    ...pickBy(entityConfigs, config => !includes([
      "ImpactStory", "Benefit", "BenefitType", "InfluencingFactor", "Challenge", 
      "OfferingType", "MaturityStage", 
      "ProjectType", 
    ], config.name)),
    DisturbanceType,
    EcologicalPrinciple,
    ErrorModel, 
    ImpactType, 
    Genus,
    Methodology, 
    MethodologyType, 
    Motivation, 
    Observation, 
    OrganisationPledge,
    PledgeMetric,
    Reason, 
    RegionType,
    RestorationProject, 
    Species, 
    SpeciesType,
    TransplantAdherenceMethod, 
    TransplantLifeStage, 
    TransplantSource, 
  },
  appSectionConfigs,
  options: {
    systemsMapping: 'full',
    shapeLogoLabel: 'oceans',
    shapeLogoIconColor: 'primary',
    shapeLogoIcon: Icons.Platforms.Ecosystems,
    entityConnectionTypeSets,
  },
  components: {
    Logo: ApplicationLogo,
    LogoInverted: ApplicationLogoInverted,
  },
});


/* ---- Patch Entities definitions afterwards to ensure sub-props are already set --- */

const entities = application.entities;

// -- ensure that traffic control team is not shown in lists
entities.Team.list.filterItems = (items) => filter(items, item => item.slug !== 'traffic-control');

// -- show Traffic controllers
entities.Team.list.postBanner = <WorkstreamTrafficControl />;
